import React, { useEffect, useState } from 'react'
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, CreatePasswordForm, Icon, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { secureTokenStore } from '@store'
import {
  clearJoinDraft,
  fakeLinkedInCode,
  isValidEmail,
  linkedinAuthorizeUrl,
  linkedinRedirectUri,
  loadJoinDraft,
  saveJoinDraft,
  translateApiError,
  getCommonStyles,
  eventPriceLabel,
  formatEventPrice,
} from '@utils'
import {
  type EventSession,
  type KtEvent,
  fetchAccountSecurity,
  useCompleteEventSignIn,
  useEventsConfig,
  useJoinEventWithEmail,
  useJoinEventWithLinkedIn,
  useRequestEmailCode,
  useSignInWithEmailCode,
  useStartEventPayment,
} from '@hooks'
import { EventConsentNotice } from './EventConsentNotice'

type Step = 'choose' | 'form' | 'code' | 'fakeLinkedin' | 'signIn' | 'signInCode' | 'password'

interface JoinPanelProps {
  event: KtEvent
  /** Called once the person is registered and logged in. */
  onJoined?: (session: EventSession) => void
}

interface FormState {
  firstName: string
  lastName: string
  jobTitle: string
  company: string
  email: string
}

const EMPTY_FORM: FormState = { firstName: '', lastName: '', jobTitle: '', company: '', email: '' }

/** What survives a page reload (see joinDraft). */
interface JoinDraft {
  step: Step
  form: FormState
  code: string
  pendingSession: EventSession | null
}

/**
 * Logged-out sign-up for an event, in the order of the product brief:
 * one-click LinkedIn first, "Je n'ai pas LinkedIn" (name, job, email + code)
 * second, and "Déjà un compte ?" (emailed code, no password) last.
 */
export function JoinPanel({ event, onJoined }: JoinPanelProps) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const isPaid = event.priceCents !== null
  const price = eventPriceLabel(event, '', currentLanguage)
  const draftKey = `event:${event.code}`
  // Read once, at mount: a reload lands back on the step (and the typed values) it left.
  const [draft] = useState(() => loadJoinDraft<JoinDraft>(draftKey))
  const [step, setStep] = useState<Step>(draft?.step ?? 'choose')
  const [form, setForm] = useState<FormState>(draft?.form ?? EMPTY_FORM)
  const [code, setCode] = useState(draft?.code ?? '')
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | 'code', string>>>({})
  const [apiError, setApiError] = useState('')
  const [pendingSession, setPendingSession] = useState<EventSession | null>(
    draft?.pendingSession ?? null,
  )

  useEffect(() => {
    if (step === 'choose') clearJoinDraft(draftKey)
    else saveJoinDraft(draftKey, { step, form, code, pendingSession } satisfies JoinDraft)
  }, [draftKey, step, form, code, pendingSession])

  const { data: config } = useEventsConfig()
  const requestCode = useRequestEmailCode()
  const joinWithEmail = useJoinEventWithEmail()
  const joinWithLinkedIn = useJoinEventWithLinkedIn()
  const signInWithCode = useSignInWithEmailCode()
  const completeSignIn = useCompleteEventSignIn()
  const { startPayment } = useStartEventPayment()

  const busy =
    requestCode.isPending ||
    joinWithEmail.isPending ||
    joinWithLinkedIn.isPending ||
    signInWithCode.isPending
  const set = (key: keyof FormState) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))
  const showError = (error: unknown) => setApiError(translateApiError(t, error).message)

  const finish = async (session: EventSession) => {
    clearJoinDraft(draftKey)
    await completeSignIn(session)
    onJoined?.(session)
    // Paid event: identity done, next step is the payment page.
    if (session.attendance?.paymentRequired) await startPayment({ id: event.id })
  }

  /**
   * After an emailed-code sign-up / sign-in the account may have no password
   * yet: offer to create one before entering the app. Logging in flips this
   * panel away, so the tokens are stored first and the login is completed once
   * the step is done. Never blocks the sign-in if the check fails.
   */
  const finishWithPasswordStep = async (session: EventSession) => {
    try {
      await secureTokenStore.setTokens(session.accessToken, session.refreshToken)
      const security = await fetchAccountSecurity()
      if (!security.hasPassword) {
        setPendingSession(session)
        setStep('password')
        return
      }
    } catch {
      // Optional step: fall through to a normal sign-in.
    }
    await finish(session)
  }

  const finishPasswordStep = () => {
    if (pendingSession) void finish(pendingSession)
  }

  if (!event.registrationOpen || event.isFull) {
    return (
      <View style={styles.panel}>
        <Text type="titleBold">{t('events.join.title')}</Text>
        <Text type="body" color={theme.colors.error}>
          {event.isFull
            ? t(event.attendeeCount === 0 ? 'events.join.fullHeld' : 'events.join.full')
            : t('events.join.closed')}
        </Text>
      </View>
    )
  }

  const validateForm = (): boolean => {
    const next: typeof errors = {}
    if (!form.firstName.trim()) next.firstName = t('events.join.required')
    if (!form.lastName.trim()) next.lastName = t('events.join.required')
    if (form.jobTitle.trim().length < 2) next.jobTitle = t('events.join.required')
    if (!isValidEmail(form.email)) next.email = t('events.join.invalidEmail')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const sendCode = (purpose: 'join' | 'sign-in', nextStep: Step) => {
    setApiError('')
    requestCode.mutate(
      { email: form.email.trim(), purpose, eventCode: event.code },
      { onSuccess: () => setStep(nextStep), onError: showError },
    )
  }

  const startLinkedIn = () => {
    setApiError('')
    if (config?.linkedin.fakeMode || !config?.linkedin.clientId) {
      setStep('fakeLinkedin')
      return
    }
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign(
        linkedinAuthorizeUrl({
          clientId: config.linkedin.clientId,
          redirectUri: linkedinRedirectUri(window.location.origin),
          state: event.code,
        }),
      )
    }
  }

  const submitFakeLinkedIn = () => {
    const next: typeof errors = {}
    if (!form.firstName.trim()) next.firstName = t('events.join.required')
    if (!isValidEmail(form.email)) next.email = t('events.join.invalidEmail')
    setErrors(next)
    if (Object.keys(next).length > 0) return
    joinWithLinkedIn.mutate(
      {
        eventCode: event.code,
        authorizationCode: fakeLinkedInCode({
          email: form.email.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
        }),
        // React Native defines `window` but not `window.location`.
        redirectUri: linkedinRedirectUri(
          (typeof window !== 'undefined' && window.location?.origin) || 'http://localhost',
        ),
      },
      { onSuccess: finish, onError: showError },
    )
  }

  const header = (title: string, subtitle?: string) => (
    <View style={styles.header}>
      {step !== 'choose' ? (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => {
            setStep('choose')
            setApiError('')
          }}
          style={styles.back}
        >
          <Icon name="arrowLeft" size={16} color={theme.colors.primary} />
          <Text type="bodySmallBold" color={theme.colors.primary} style={styles.backText}>
            {t('events.join.back')}
          </Text>
        </TouchableOpacity>
      ) : null}
      <Text type="titleBold">{title}</Text>
      {subtitle ? (
        <Text type="body" color={theme.colors.greyBlue} style={styles.subtitle}>
          {subtitle}
        </Text>
      ) : null}
    </View>
  )

  const errorLine = apiError ? (
    <Text type="bodySmall" color={theme.colors.error} style={styles.apiError}>
      {apiError}
    </Text>
  ) : null

  if (step === 'password') {
    return (
      <View style={styles.panel}>
        <View style={styles.header}>
          <Text type="titleBold">{t('createPassword.title')}</Text>
          <Text type="body" color={theme.colors.greyBlue} style={styles.subtitle}>
            {t('createPassword.subtitle')}
          </Text>
        </View>
        <CreatePasswordForm onSaved={finishPasswordStep} onLater={finishPasswordStep} />
      </View>
    )
  }

  if (step === 'choose') {
    return (
      <View style={styles.panel}>
        {header(t('events.join.title'), t('events.join.subtitle'))}
        {isPaid ? (
          <View style={styles.paidNotice} testID="join-paid-notice">
            <Icon name="event" size={18} color={theme.colors.primary} />
            <View style={styles.paidNoticeText}>
              <Text type="bodySmallBold" color={theme.colors.primary}>
                {t('events.join.paidNotice', { price })}
              </Text>
              {event.serviceFeeCents > 0 ? (
                <Text type="bodyExtraSmall" color={theme.colors.secondary}>
                  {t('events.public.serviceFee', {
                    fee: formatEventPrice(event.serviceFeeCents, currentLanguage, event.currency),
                  })}
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}
        <TouchableOpacity
          accessibilityRole="button"
          onPress={startLinkedIn}
          style={styles.linkedinButton}
        >
          <Icon name="linkedin" size={20} color={theme.colors.white} />
          <Text type="bodyBold" color={theme.colors.white} style={styles.linkedinLabel}>
            {t('events.join.linkedin')}
          </Text>
        </TouchableOpacity>
        <Button
          title={t('events.join.noLinkedin')}
          unfilled
          onPress={() => setStep('form')}
          containerStyle={styles.fullWidth}
        />
        <View style={styles.signInRow}>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('events.join.haveAccount')}
          </Text>
          <TouchableOpacity accessibilityRole="button" onPress={() => setStep('signIn')}>
            <Text type="bodySmallBold" color={theme.colors.primary} style={styles.backText}>
              {t('events.join.signIn')}
            </Text>
          </TouchableOpacity>
        </View>
        <EventConsentNotice event={event} />
        {errorLine}
      </View>
    )
  }

  if (step === 'fakeLinkedin') {
    return (
      <View style={styles.panel}>
        {header(t('events.join.fakeTitle'), t('events.join.fakeHint'))}
        <View style={styles.row}>
          <TextInput
            placeholder={t('events.join.firstName')}
            value={form.firstName}
            onChangeText={set('firstName')}
            errorMessage={errors.firstName}
            containerStyle={styles.half}
          />
          <TextInput
            placeholder={t('events.join.lastName')}
            value={form.lastName}
            onChangeText={set('lastName')}
            containerStyle={styles.half}
          />
        </View>
        <TextInput
          placeholder={t('events.join.email')}
          value={form.email}
          onChangeText={set('email')}
          autoCapitalize="none"
          keyboardType="email-address"
          errorMessage={errors.email}
        />
        <EventConsentNotice event={event} />
        {errorLine}
        <TouchableOpacity
          accessibilityRole="button"
          onPress={submitFakeLinkedIn}
          disabled={busy}
          style={[styles.linkedinButton, busy && styles.busy]}
        >
          <Icon name="linkedin" size={20} color={theme.colors.white} />
          <Text type="bodyBold" color={theme.colors.white} style={styles.linkedinLabel}>
            {t('events.join.fakeContinue')}
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  if (step === 'form') {
    return (
      <View style={styles.panel}>
        {header(t('events.join.noLinkedin'), t('events.join.subtitle'))}
        <View style={styles.row}>
          <TextInput
            placeholder={t('events.join.firstName')}
            value={form.firstName}
            onChangeText={set('firstName')}
            errorMessage={errors.firstName}
            containerStyle={styles.half}
          />
          <TextInput
            placeholder={t('events.join.lastName')}
            value={form.lastName}
            onChangeText={set('lastName')}
            errorMessage={errors.lastName}
            containerStyle={styles.half}
          />
        </View>
        <TextInput
          placeholder={t('events.join.jobTitle')}
          value={form.jobTitle}
          onChangeText={set('jobTitle')}
          errorMessage={errors.jobTitle}
          iconRight="work"
        />
        <TextInput
          placeholder={t('events.join.company')}
          value={form.company}
          onChangeText={set('company')}
        />
        <TextInput
          placeholder={t('events.join.email')}
          value={form.email}
          onChangeText={set('email')}
          autoCapitalize="none"
          keyboardType="email-address"
          errorMessage={errors.email}
          iconRight="message"
        />
        <EventConsentNotice event={event} />
        {errorLine}
        <Button
          title={t('events.join.sendCode')}
          disabled={busy}
          onPress={() => validateForm() && sendCode('join', 'code')}
          containerStyle={styles.fullWidth}
        />
      </View>
    )
  }

  if (step === 'code' || step === 'signInCode') {
    const isSignIn = step === 'signInCode'
    const submit = () => {
      if (!/^\d{6}$/.test(code.replace(/\s+/g, ''))) {
        setErrors({ code: t('events.join.codeLabel') })
        return
      }
      setApiError('')
      if (isSignIn) {
        signInWithCode.mutate(
          { email: form.email.trim(), code },
          { onSuccess: finishWithPasswordStep, onError: showError },
        )
        return
      }
      joinWithEmail.mutate(
        {
          eventCode: event.code,
          email: form.email.trim(),
          code,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          jobTitle: form.jobTitle.trim(),
          company: form.company.trim() || undefined,
        },
        { onSuccess: finishWithPasswordStep, onError: showError },
      )
    }
    return (
      <View style={styles.panel}>
        {header(
          t('events.join.codeLabel'),
          t('events.join.codeSentTo', { email: form.email.trim() }),
        )}
        <TextInput
          testID="event-code-input"
          placeholder="000000"
          value={code}
          onChangeText={(value) => setCode(value.replace(/[^\d]/g, '').slice(0, 6))}
          keyboardType="number-pad"
          maxLength={6}
          errorMessage={errors.code}
          onSubmitEditing={submit}
        />
        {errorLine}
        <Button
          title={
            isSignIn
              ? t('events.join.signIn')
              : isPaid
                ? t('events.join.confirmPaid')
                : t('events.join.confirm')
          }
          disabled={busy}
          onPress={submit}
          containerStyle={styles.fullWidth}
        />
        <TouchableOpacity
          accessibilityRole="button"
          disabled={busy}
          onPress={() => sendCode(isSignIn ? 'sign-in' : 'join', step)}
        >
          <Text type="bodySmallBold" color={theme.colors.primary} center>
            {t('events.join.resend')}
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  // step === 'signIn': existing account, password-less return
  return (
    <View style={styles.panel}>
      {header(t('events.join.signIn'), t('events.join.haveAccount'))}
      <TextInput
        placeholder={t('events.join.email')}
        value={form.email}
        onChangeText={set('email')}
        autoCapitalize="none"
        keyboardType="email-address"
        errorMessage={errors.email}
        iconRight="message"
      />
      {errorLine}
      <Button
        title={t('events.join.sendCode')}
        disabled={busy}
        onPress={() => {
          if (!isValidEmail(form.email)) {
            setErrors({ email: t('events.join.invalidEmail') })
            return
          }
          setErrors({})
          sendCode('sign-in', 'signInCode')
        }}
        containerStyle={styles.fullWidth}
      />
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    panel: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xxl,
      gap: theme.spacing.md,
      ...getCommonStyles(theme).shadow,
    },
    header: { gap: theme.spacing.xs, marginBottom: theme.spacing.xs },
    subtitle: { lineHeight: 21 },
    back: { flexDirection: 'row', alignItems: 'center', marginBottom: theme.spacing.xs },
    backText: { marginLeft: theme.spacing.xs },
    linkedinButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      height: 50,
      borderRadius: 25,
      backgroundColor: theme.colors.linkedin,
    },
    linkedinLabel: { marginLeft: theme.spacing.sm },
    busy: { opacity: 0.6 },
    fullWidth: { width: '100%' },
    signInRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    paidNotice: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.sidebar,
      borderRadius: theme.border.medium,
      padding: theme.spacing.md,
    },
    paidNoticeText: { marginLeft: theme.spacing.sm, flexShrink: 1 },
    apiError: { marginTop: -theme.spacing.xs },
    row: { flexDirection: 'row', gap: theme.spacing.sm },
    half: { flex: 1 },
  })
